import { defineStore } from 'pinia';
import useAuthClient from '@/clients/authClient.js';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import useCookiesHelper from '@/composables/useCookiesHelper.js';

export const useAuthStore = defineStore(
  'useAuthStore',
  () => {
    const authClient = useAuthClient();
    const cookiesHelper = useCookiesHelper();

    const user = ref(null);
    const accessToken = ref(null);
    const isAuthenticated = ref(false);
    const isDeactivated = ref(false);
    // True when the user is signed in but has no confirmed (active) subscription.
    const requiresSubscription = ref(false);
    const router = useRouter();

    const setAuthentication = (token) => {
      accessToken.value = token;
    };

    const setUser = (userData) => {
      user.value = userData ? { ...userData } : null;
      isAuthenticated.value = !!userData;

      if (userData) {
        const isAdmin = (userData.roles ?? []).includes('admin');
        // Only block when the backend explicitly reports no active subscription.
        requiresSubscription.value = !isAdmin && userData.has_active_subscription === false;
      } else {
        requiresSubscription.value = false;
      }
    };

    const login = async (email, password) => {
      const response = await authClient.login(email, password);
      isAuthenticated.value = true;
      isDeactivated.value = false;
      setAuthentication(response.access_token);
      setUser(response.user);

      // Clear impersonation state on login
      clearImpersonation();
    };

    const clearImpersonation = () => {
      // Remove impersonation cookie
      cookiesHelper.removeCookie('impersonator_id');

      // Clear impersonation from persisted store
      // This will be handled by resetting the impersonation store's state
      const impersonationData = localStorage.getItem('impersonationStore');
      if (impersonationData) {
        try {
          const parsed = JSON.parse(impersonationData);
          parsed.impersonatedUser = null;
          localStorage.setItem('impersonationStore', JSON.stringify(parsed));
        } catch (err) {
          console.error('Error clearing impersonation:', err);
        }
      }
    };

    const register = async (params) => {
      return await authClient.register(params);
    };

    const checkUserIsAuthenticated = async () => {
      try {
        const userData = await authClient.me();
        setUser(userData.data);
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    };

    const logout = () => {
      router.push('/');
      setAuthentication(null);
      setUser(null);
    };

    return {
      isAuthenticated,
      isDeactivated,
      requiresSubscription,
      user,
      accessToken,
      setAuthentication,
      register,
      login,
      checkUserIsAuthenticated,
      setUser,
      logout
    };
  },
  { persist: true }
);
